import {
  snapTimeToFrame,
  timeToTimelinePixels,
  timelinePixelsToTime,
  VIDEO_EDITOR_FPS,
  VIDEO_EDITOR_TIMELINE_SCALE,
  VIDEO_EDITOR_TIMELINE_SNAP_PIXELS,
  VIDEO_EDITOR_TIMELINE_START_LEFT,
  VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT,
  VIDEO_EDITOR_TRACK_TYPE,
} from "./constants";
import {
  getImageTracksOrdered,
  getAudioTracksOrdered,
  getVideoTracksOrdered,
  getClipPackingLaneIndex,
} from "./clipTimelineUtils";
import { getClipEndOnTimeline, getClipTimelineDuration } from "./projectSchema";
import {
  getTimelineRowScrollOffset,
  resolveTimelineRowAtOffset,
} from "./timelineLayout";
import { visualIndexToPackingLane } from "./timelineLanePacking";

export const CLIP_INTERACTION_MODE = {
  MOVE: "move",
  RESIZE_LEFT: "resize-left",
  RESIZE_RIGHT: "resize-right",
};

export const getEditAreaNode = (timelineAreaNode) =>
  timelineAreaNode?.querySelector?.(".timeline-editor-edit-area") || null;

export const getTimelineScrollNode = (timelineAreaNode) =>
  timelineAreaNode?.querySelector?.(
    ".timeline-editor-edit-area .ReactVirtualized__Grid"
  ) || getEditAreaNode(timelineAreaNode);

export const getPointerTimelineMetrics = ({
  timelineAreaNode,
  clientX,
  clientY,
}) => {
  const editArea = getEditAreaNode(timelineAreaNode);
  const scrollNode = getTimelineScrollNode(timelineAreaNode);
  const areaRect = timelineAreaNode?.getBoundingClientRect?.();
  const editRect = editArea?.getBoundingClientRect?.();
  const scrollLeft = scrollNode?.scrollLeft || 0;
  const scrollTop = getTimelineRowScrollOffset(timelineAreaNode);

  const xInContent =
    clientX - (editRect?.left ?? areaRect?.left ?? 0) + scrollLeft;
  const offsetY =
    clientY -
    (editRect?.top ?? areaRect?.top ?? 0) +
    scrollTop +
    VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT;

  return {
    editArea,
    editRect,
    areaRect,
    scrollLeft,
    scrollTop,
    xInContent,
    offsetY,
    clientX,
    clientY,
  };
};

export const pointerToTime = ({
  xInContent,
  scaleWidth,
  scale = VIDEO_EDITOR_TIMELINE_SCALE,
  startLeft = VIDEO_EDITOR_TIMELINE_START_LEFT,
}) =>
  timelinePixelsToTime({
    pixels: xInContent,
    scaleWidth,
    scale,
    startLeft,
  });

export const collectSnapTargets = ({ edl, excludeClipIds, excludeClipId, playhead }) => {
  const excluded = new Set(
    (excludeClipIds || (excludeClipId ? [excludeClipId] : [])).filter(Boolean)
  );
  const targets = [Math.max(0, Number(playhead) || 0)];
  Object.values(edl?.clips?.byId || {}).forEach((clip) => {
    if (!clip || excluded.has(clip.id)) {
      return;
    }
    targets.push(Number(clip.position) || 0);
    targets.push(getClipEndOnTimeline({ clip }));
  });
  return targets;
};

export const snapDragTime = ({
  time,
  fps = VIDEO_EDITOR_FPS,
  scaleWidth,
  scale = VIDEO_EDITOR_TIMELINE_SCALE,
  snapTargets = [],
}) => {
  const frameSnapped = snapTimeToFrame({ time, fps });
  const threshold =
    (VIDEO_EDITOR_TIMELINE_SNAP_PIXELS / Math.max(scaleWidth, 1)) * scale;

  let nextTime = frameSnapped;
  let bestDistance = threshold;

  snapTargets.forEach((target) => {
    const distance = Math.abs(frameSnapped - target);
    if (distance <= bestDistance) {
      nextTime = target;
      bestDistance = distance;
    }
  });

  return Math.max(0, nextTime);
};

export const getPreferredPackingLane = ({ edl, clip, targetRow }) => {
  if (!targetRow || !clip) {
    return getClipPackingLaneIndex({ edl, clipId: clip?.id });
  }

  if (
    clip.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE &&
    targetRow.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE
  ) {
    const imageTracks = getImageTracksOrdered({ edl });
    const visualIndex = Math.max(
      0,
      imageTracks.findIndex((track) => track.id === targetRow.trackId)
    );
    return visualIndexToPackingLane({
      type: VIDEO_EDITOR_TRACK_TYPE.IMAGE,
      visualIndex,
      laneCount: Math.max(imageTracks.length, 1),
    });
  }

  if (
    clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO &&
    targetRow.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
  ) {
    const audioTracks = getAudioTracksOrdered({ edl });
    const visualIndex = Math.max(
      0,
      audioTracks.findIndex((track) => track.id === targetRow.trackId)
    );
    return visualIndexToPackingLane({
      type: VIDEO_EDITOR_TRACK_TYPE.AUDIO,
      visualIndex,
      laneCount: Math.max(audioTracks.length, 1),
    });
  }

  if (
    clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO &&
    targetRow.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
  ) {
    const videoTracks = getVideoTracksOrdered({ edl });
    const visualIndex = Math.max(
      0,
      videoTracks.findIndex((track) => track.id === targetRow.trackId)
    );
    return visualIndexToPackingLane({
      type: VIDEO_EDITOR_TRACK_TYPE.VIDEO,
      visualIndex,
      laneCount: Math.max(videoTracks.length, 1),
    });
  }

  return getClipPackingLaneIndex({ edl, clipId: clip.id });
};

export const resolveDropTarget = ({ clip, targetRow }) => {
  if (!clip || !targetRow) {
    return { kind: "none" };
  }

  if (
    clip.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE &&
    targetRow.type === VIDEO_EDITOR_TRACK_TYPE.IMAGE
  ) {
    return { kind: "image-lane", trackId: targetRow.trackId };
  }

  if (
    clip.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO &&
    targetRow.type === VIDEO_EDITOR_TRACK_TYPE.AUDIO
  ) {
    return { kind: "audio-lane", trackId: targetRow.trackId };
  }

  if (
    clip.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO &&
    targetRow.type === VIDEO_EDITOR_TRACK_TYPE.VIDEO
  ) {
    return { kind: "video-lane", trackId: targetRow.trackId };
  }

  return { kind: "none" };
};

export const resolveRowAtPointer = ({ edl, timelineAreaNode, clientY }) => {
  const metrics = getPointerTimelineMetrics({
    timelineAreaNode,
    clientX: 0,
    clientY,
  });
  return resolveTimelineRowAtOffset({ edl, offsetY: metrics.offsetY });
};

export const getGhostGeometry = ({
  start,
  duration,
  row,
  scaleWidth,
  scrollLeft,
  scrollTop,
}) => {
  const width = timeToTimelinePixels({
    time: duration,
    scaleWidth,
  });
  const left =
    timeToTimelinePixels({
      time: start,
      scaleWidth,
    }) - scrollLeft;
  const top = (row?.top || VIDEO_EDITOR_TIMELINE_TIME_AREA_HEIGHT) - scrollTop;

  return {
    left,
    top,
    width: Math.max(width, 8),
    height: row?.height || 29,
  };
};

export const getClipDuration = (clip) =>
  Math.max(getClipTimelineDuration({ clip }), 0);
